import {ActionIcon, Tooltip} from '@mantine/core';
import Image from 'next/image';
import type {FC} from 'react';
import {MdBusiness, MdChecklist, MdDelete, MdSwapVert} from 'react-icons/md';
import PREPARE_WITH_AI from '../../../../../../../public/next-image/reportAnalytics/logo.png';
import {
  COLOR_BLACK_2,
  COLOR_PRIMARY_1,
} from '../../../../../../config/constants/colorCodes';
import {
  ENTRY_MANAGEMENTS_TODO,
  REPORT_ANALYTICS_SEARCH,
} from '../../../../../../config/constants/routes';
import {isReleasedCareerAdviser} from '../../../../../../config/unReleasePage';
import {CommonLink} from '../../../../../../shared/components/CommonLink';
import {getAIGradient} from '../../../../../reportAnalytics/usecase/getAIGradient';
import type {OverallStatusType} from '../../../../constants';
import {CompanyNameText} from './CompanyNameText';
import {InterviewTrainingLink} from './InterviewTrainingLink';
import {OverallStatusChangeButton} from './OverallStatusChangeButton';

/**
 * 削除ボタンコンポーネント
 * @param onClick - 削除ボタンクリック時のハンドラー
 * @param withTooltip - ツールチップの表示有無（PC版はtrue、SP版はfalse）
 */
const DeleteButton: FC<{
  onClick: () => void;
  withTooltip: boolean;
}> = ({onClick, withTooltip}) => {
  const button = (
    <ActionIcon
      variant="transparent"
      onClick={onClick}
      className="hover:opacity-60"
    >
      <MdDelete size={24} color={COLOR_BLACK_2} />
    </ActionIcon>
  );

  return withTooltip ? (
    <Tooltip label="選考管理を削除" withArrow withinPortal>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

/**
 * 並び替えモード切り替えボタンコンポーネント
 * SP版で並び替えモードのON/OFFを切り替える
 * @param isActive - 並び替えモードが有効かどうか
 * @param onClick - ボタンクリック時のハンドラー
 */
const ReorderModeButton: FC<{
  isActive: boolean;
  onClick: () => void;
}> = ({isActive, onClick}) => {
  const buttonClassName = isActive
    ? 'pc:hover:opacity-60 bg-primary-light rounded-full'
    : 'pc:hover:opacity-60';
  const iconColor = isActive ? COLOR_PRIMARY_1 : COLOR_BLACK_2;

  return (
    <Tooltip
      label={isActive ? '並び替えを終了' : '並び替え'}
      withArrow
      withinPortal
    >
      <ActionIcon
        variant="transparent"
        onClick={onClick}
        className={buttonClassName}
      >
        <MdSwapVert size={24} color={iconColor} />
      </ActionIcon>
    </Tooltip>
  );
};

/**
 * リンクセクション（選考体験記 + AI選考対策）
 * PC版で表示される企業関連リンク
 * @param companyId - 企業ID
 * @param hasReports - 選考体験記の有無
 * @param hasAiReportSummary - AI選考対策データの有無
 */
const LinkSection: FC<{
  companyId?: number;
  hasReports: boolean;
  hasAiReportSummary: boolean;
}> = ({companyId, hasReports, hasAiReportSummary}) => {
  // 表示すべきリンクがない場合は何も表示しない
  if (!hasReports && !hasAiReportSummary && !companyId) {
    return null;
  }

  return (
    <div className="flex-shrink-0 flex items-center justify-end gap-8">
      {isReleasedCareerAdviser() && hasAiReportSummary && companyId && (
        <InterviewTrainingLink companyId={companyId} />
      )}
      {hasReports && (
        <CommonLink
          href={`/company/${companyId}/experiences`}
          className="text-sm font-bold text-primary border-2 border-primary rounded-full p-8 whitespace-nowrap hover:opacity-60 hover:cursor-pointer"
        >
          選考体験記
        </CommonLink>
      )}
      {hasAiReportSummary && (
        <CommonLink
          href={REPORT_ANALYTICS_SEARCH}
          className="rounded-[32px] h-[36px] px-12 flex items-center justify-center cursor-pointer"
          style={{
            background: getAIGradient(),
          }}
        >
          <Image
            src={PREPARE_WITH_AI}
            className="w-[54px] h-[20px]"
            alt="AI選考対策"
          />
        </CommonLink>
      )}
    </div>
  );
};

/**
 * 企業関連リンクセクション（企業詳細 + ToDo）
 * @param companyId - 企業ID
 * @param entryManagementId - エントリー管理ID
 * @param hasTodosForEntryManagement - エントリー管理に紐づくToDoの有無判定関数
 */
const CompanyLinksSection: FC<{
  companyId?: number;
  entryManagementId?: number;
  hasTodosForEntryManagement?: (entryManagementId: number) => boolean;
}> = ({companyId, entryManagementId, hasTodosForEntryManagement}) => {
  const showCompanyLink = companyId !== undefined;
  const showTodoLink =
    entryManagementId !== undefined &&
    hasTodosForEntryManagement?.(entryManagementId);

  if (!showCompanyLink && !showTodoLink) {
    return null;
  }

  return (
    <div className="w-full flex justify-center items-center gap-12 mt-4">
      {showCompanyLink && (
        <CommonLink
          href={`/company/${companyId}`}
          className="flex items-center gap-4 text-base text-link-1 underline pc:hover:opacity-80"
        >
          <MdBusiness size={20} aria-hidden="true" focusable={false} />
          企業情報
        </CommonLink>
      )}
      {showTodoLink && (
        <CommonLink
          href={`${ENTRY_MANAGEMENTS_TODO}?entry_management_id=${entryManagementId}`}
          className="flex items-center gap-4 text-base text-link-1 underline pc:hover:opacity-80"
        >
          <MdChecklist size={20} aria-hidden="true" focusable={false} />
          この企業に紐づくToDo
        </CommonLink>
      )}
    </div>
  );
};

type Props = {
  /** 企業ID */
  companyId?: number;
  /** エントリー管理ID */
  entryManagementId?: number;
  /** 企業名 */
  companyName: string;
  /** 企業ロゴURL */
  companyLogoUrl?: string;
  /** 選考管理削除ハンドラー */
  onDeleteClick: () => void;
  /** 企業名が編集可能かどうか */
  companyNameEditable?: boolean;
  /** 企業名保存ハンドラー。成功時はtrue、失敗時はfalseを返す */
  onCompanyNameSave?: (
    companyName: string,
    companyId?: number,
  ) => Promise<boolean>;
  /** 企業リスト（Autocomplete用） */
  companyList?: {id: number; value: string}[];
  /** 選考体験記の有無 */
  hasReports?: boolean;
  /** AI選考対策データの有無 */
  hasAiReportSummary?: boolean;
  /** エントリー管理に紐づくToDoの有無判定関数 */
  hasTodosForEntryManagement?: (entryManagementId: number) => boolean;
  /** 全体ステータス */
  overallStatus: OverallStatusType;
  /** 内定獲得ステータスに変更 */
  onChangeToCompleteStatus?: () => void;
  /** 辞退ステータスに変更 */
  onChangeToArchiveStatus?: () => void;
  /** 落選ステータスに変更 */
  onChangeToRejectedStatus?: () => void;
  /** エントリー完了状態 */
  hasCompletedEntry: boolean;
  /** 未エントリー状態に変更（hasCompletedEntry: false + overallStatus: active） */
  onChangeHasCompletedEntryFalse?: () => void;
  /** エントリー済み状態に変更（hasCompletedEntry: true + overallStatus: active） */
  onChangeHasCompletedEntryTrue?: () => void;
  /** SP表示かどうか */
  isSp?: boolean;
  /** 並び替えモードかどうか */
  isReorderMode?: boolean;
  /** 並び替えモードの切り替え */
  onToggleReorderMode?: () => void;
  /** 並び替え可能かどうか（ステップが2件以上） */
  canReorder?: boolean;
};

export const DetailTitleSection: React.FC<Props> = ({
  companyId,
  entryManagementId,
  companyName,
  companyLogoUrl,
  onDeleteClick,
  companyNameEditable = false,
  onCompanyNameSave,
  companyList = [],
  hasReports = false,
  hasAiReportSummary = false,
  hasTodosForEntryManagement,
  overallStatus,
  onChangeToCompleteStatus,
  onChangeToArchiveStatus,
  onChangeToRejectedStatus,
  hasCompletedEntry,
  onChangeHasCompletedEntryFalse,
  onChangeHasCompletedEntryTrue,
  isSp = false,
  isReorderMode = false,
  onToggleReorderMode,
  canReorder = false,
}) => {
  return (
    <div className="flex flex-col justify-center items-center gap-8">
      <div className="flex flex-col pc:flex-row items-center justify-center pc:justify-between gap-8 w-full">
        <div className="w-full pc:w-auto flex flex-wrap gap-8 justify-start items-center">
          <OverallStatusChangeButton
            currentStatus={overallStatus}
            onCompleteClick={onChangeToCompleteStatus}
            onArchiveClick={onChangeToArchiveStatus}
            onRejectedClick={onChangeToRejectedStatus}
            hasCompletedEntry={hasCompletedEntry}
            onChangeHasCompletedEntryFalse={onChangeHasCompletedEntryFalse}
            onChangeHasCompletedEntryTrue={onChangeHasCompletedEntryTrue}
          />
          <div className="flex flex-col gap-4 justify-center">
            <CompanyNameText
              companyLogoUrl={companyLogoUrl}
              companyName={companyName}
              editable={companyNameEditable}
              onSave={onCompanyNameSave}
              companyList={companyList}
            />
            {!companyId && (
              <p className="text-sm text-notification-1">
                外資就活ドットコムに登録のない企業です。企業名をクリックすると編集ができます。
              </p>
            )}
          </div>
        </div>

        <div className="w-full pc:w-auto flex items-center justify-end pc:justify-center gap-8 pc:flex-shrink-0">
          <LinkSection
            companyId={companyId}
            hasReports={hasReports}
            hasAiReportSummary={hasAiReportSummary}
          />
          {/* SP: 並び替えモード切り替えボタン */}
          {isSp && canReorder && onToggleReorderMode && (
            <ReorderModeButton
              isActive={isReorderMode}
              onClick={onToggleReorderMode}
            />
          )}
          <DeleteButton onClick={onDeleteClick} withTooltip={!isSp} />
        </div>
      </div>
      <CompanyLinksSection
        companyId={companyId}
        entryManagementId={entryManagementId}
        hasTodosForEntryManagement={hasTodosForEntryManagement}
      />
    </div>
  );
};
